import z from "zod";

//* Request schema for sending recovery password email
export const sendEmailRecoveryPasswordSchema = z.object({
  cpf: z
    .string()
    .min(11, "O CPF deve ter pelo menos 11 caracteres")
    .max(14, "O CPF deve ter no máximo 14 caracteres")
    .regex(/^\d{3}\.\d{3}\.\d{3}-\d{2}$|^\d{11}$/, "Formato de CPF inválido"),
});

//* Request schema for validating recovery token
export const validateTokenRecoveryPasswordSchema = z.object({
  token: z
    .string()
    .min(1, "Token é obrigatório")
    .length(36, "Token deve ter exatamente 36 caracteres"),
});

//* Request schema for changing recovery password
export const changeRecoveryPasswordSchema = z.object({
  token: z
    .string()
    .min(1, "Token é obrigatório")
    .length(36, "Token deve ter exatamente 36 caracteres"),
  password: z
    .string()
    .min(1, "Nova senha é obrigatória")
    .min(8, "Nova senha deve ter pelo menos 8 caracteres")
    .max(128, "Nova senha deve ter no máximo 128 caracteres")
    .regex(/[a-z]/, "Nova senha deve conter pelo menos uma letra minúscula")
    .regex(/[A-Z]/, "Nova senha deve conter pelo menos uma letra maiúscula")
    .regex(/[0-9]/, "Nova senha deve conter pelo menos um número")
    .regex(/[^a-zA-Z0-9]/, "Nova senha deve conter pelo menos um símbolo especial")
    .refine((password) => {
      // Check for sequential numbers (123, 234, etc.)
      const sequentialNumbers = /012|123|234|345|456|567|678|789|890/;
      return !sequentialNumbers.test(password);
    }, "Nova senha não deve conter números em sequência")
    .refine((password) => {
      // Check for sequential letters (abc, def, etc.)
      const sequentialLetters = /abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz/i;
      return !sequentialLetters.test(password);
    }, "Nova senha não deve conter letras em sequência")
    .refine((password) => {
      // Check for repeated characters (aaa, 111, etc.)
      const repeatedChars = /(.)\1{2,}/;
      return !repeatedChars.test(password);
    }, "Nova senha não deve conter caracteres repetidos consecutivamente")
    .refine((password) => {
      // Check for common weak patterns
      const commonPatterns = /(password|123456|qwerty|admin|user|senha|test)/i;
      return !commonPatterns.test(password);
    }, "Nova senha não deve conter padrões comuns como 'password', '123456', 'qwerty'"),
  password_confirmation: z
    .string()
    .min(1, "Confirmação da senha é obrigatória")
    .min(8, "Confirmação da senha deve ter pelo menos 8 caracteres"),
}).refine((data) => data.password === data.password_confirmation, {
  message: "Confirmação da senha não confere",
  path: ["password_confirmation"],
});

//* Response schemas
export const sendEmailRecoveryPasswordSuccessSchema = z.object({
  result: z.boolean().refine(val => val === true, "Result must be true"),
  data: z.string(),
});

export const validateTokenRecoveryPasswordSuccessSchema = z.object({
  result: z.boolean().refine(val => val === true, "Result must be true"),
  data: z.object({
    message: z.string(),
    faculdade: z.object({
      id: z.number(),
      name: z.string(),
      logo: z.string().nullable(),
      slug: z.string(),
    }),
    user_info: z.object({
      name: z.string(),
      email: z.string().email("Email deve ser válido"),
    }),
  }),
});

export const changeRecoveryPasswordSuccessSchema = z.object({
  result: z.boolean().refine(val => val === true, "Result must be true"),
  data: z.object({
    user: z.object({
      id: z.number().positive("ID deve ser positivo"),
      name: z.string().min(1, "Nome é obrigatório"),
      email: z.string().email("Email deve ser válido"),
      numeroInscricao: z.string().min(1, "Número de inscrição é obrigatório"),
      cpf: z.string().regex(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, "CPF deve estar no formato XXX.XXX.XXX-XX"),
      celphone: z.string().nullable(),
      role: z.string().min(1, "Função é obrigatória"),
      nameSocial: z.string().nullable(),
      empresa: z.string().nullable(),
    }),
    access: z.object({
      token: z.string().min(1, "Token é obrigatório"),
      tokenType: z.literal("Bearer", { 
        errorMap: () => ({ message: "Token type deve ser Bearer" })
      }),      
      expiresAt: z.string().regex(/^\d{2}[/\\]\d{2}[/\\]\d{4} \d{2}:\d{2}$/, 
        "Data de expiração deve estar no formato DD/MM/YYYY HH:MM"),
    }),
  }),
  message: z.string().min(1, "Mensagem é obrigatória"),
});

export const RecoveryPasswordErrorSchema = z.object({
  result: z.boolean().refine(val => val === false, "Result must be false for errors"),
  data: z.array(z.any()),
  error: z.object({
    message: z.string(),
    debug: z
      .object({
        file: z.string(),
        line: z.number(),
        trace: z.array(z.any()),
      })
      .optional(),
  }),
});

//? Alternative flexible schema for backward compatibility
export const changeRecoveryPasswordFlexibleSchema = z.object({
  result: z.union([z.boolean(), z.literal(true), z.literal(false)]),
  data: z.object({
    user: z.object({
      id: z.number(),
      name: z.string(),
      email: z.string().email("Email deve ser válido"),
      numeroInscricao: z.string(),
      cpf: z.string(),
      celphone: z.string().nullable(),
      role: z.string(),
      nameSocial: z.string().nullable(),
      empresa: z.string().nullable(),
    }),
    access: z.object({
      token: z.string().min(1, "Token é obrigatório"),
      tokenType: z.string(),
      expiresAt: z.string(),
    }),
  }),
  message: z.string(),
}).refine(data => data.result === true, {
  message: "Response must indicate success",
  path: ["result"]
});

//? Type exports
export type SendEmailRecoveryPasswordRequest = z.infer<typeof sendEmailRecoveryPasswordSchema>;
export type SendEmailRecoveryPasswordSuccessResponse = z.infer<typeof sendEmailRecoveryPasswordSuccessSchema>;
export type ValidateTokenRecoveryPasswordRequest = z.infer<typeof validateTokenRecoveryPasswordSchema>;
export type ValidateTokenRecoveryPasswordSuccessResponse = z.infer<typeof validateTokenRecoveryPasswordSuccessSchema>;
export type ChangeRecoveryPasswordRequest = z.infer<typeof changeRecoveryPasswordSchema>;
export type ChangeRecoveryPasswordSuccessResponse = z.infer<typeof changeRecoveryPasswordSuccessSchema>;
export type SendEmailRecoveryPasswordErrorResponse = z.infer<
  typeof RecoveryPasswordErrorSchema
>;